/**
 * useMixer Hook
 * Handles mixer-related operations (volume, pan, mute, solo, naming)
 */

import { useCallback } from 'react';

/**
 * Hook for mixer operations
 * @param {Function} setTracks - State setter for tracks
 * @param {Function} pushUndoAction - Push action to undo stack
 * @param {Object} ActionTypes - Action type constants
 */
export function useMixer(setTracks, pushUndoAction, ActionTypes) {
  
  // Change track volume
  const handleVolumeChange = useCallback((trackId, volume) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, volume } : t
    ));
  }, [setTracks]);
  
  // Change track pan
  const handlePanChange = useCallback((trackId, pan) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, pan } : t
    ));
  }, [setTracks]);
  
  // Toggle track mute
  const handleMuteToggle = useCallback((trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, muted: !t.muted } : t
    ));
  }, [setTracks]);
  
  // Toggle track solo
  const handleSoloToggle = useCallback((trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, solo: !t.solo } : t
    ));
  }, [setTracks]);
  
  // Change track name
  const handleNameChange = useCallback((trackId, name) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, name } : t
    ));
  }, [setTracks]);
  
  // Change track color
  const handleColorChange = useCallback((trackId, color) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, color } : t
    ));
  }, [setTracks]);
  
  // Arm track for recording
  const handleArmToggle = useCallback((trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, armed: !t.armed } : t
    ));
  }, [setTracks]);
  
  return {
    handleVolumeChange,
    handlePanChange,
    handleMuteToggle,
    handleSoloToggle,
    handleNameChange,
    handleColorChange,
    handleArmToggle,
  };
}

export default useMixer;
